import { Schedule, CreateScheduleRequest, UpdateScheduleRequest } from '../models/Schedule';
import { getCurrentApi } from './api';
import { auditService } from './auditService';

// Base URL for schedule endpoints (api.ts already adds /api prefix)
const SCHEDULE_URL = '/schedule';

/**
 * Get all schedules the user has access to
 */
export const getSchedules = async (): Promise<Schedule[]> => {
  try {
    const api = getCurrentApi();
    const response = await api.get<Schedule[]>(SCHEDULE_URL);
    return response.data;
  } catch (error) {
    console.error('Error fetching schedules:', error);
    throw error;
  }
};

/**
 * Get all schedules for a specific zone
 */
export const getSchedulesForZone = async (zoneId: number): Promise<Schedule[]> => {
  try {
    const api = getCurrentApi();
    const response = await api.get<Schedule[]>(`${SCHEDULE_URL}/zone/${zoneId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching schedules for zone ${zoneId}:`, error);
    throw error;
  }
};

/**
 * Get a specific schedule by ID
 */
export const getSchedule = async (scheduleId: number): Promise<Schedule> => {
  try {
    const api = getCurrentApi();
    const response = await api.get<Schedule>(`${SCHEDULE_URL}/${scheduleId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching schedule ${scheduleId}:`, error);
    throw error;
  }
};

/**
 * Get the currently active schedule for a zone
 */
export const getCurrentScheduleForZone = async (zoneId: number): Promise<Schedule | null> => {
  try {
    const api = getCurrentApi();
    const response = await api.get<Schedule>(`${SCHEDULE_URL}/zone/${zoneId}/current`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching current schedule for zone ${zoneId}:`, error);
    return null;
  }
};

/**
 * Get effective settings for a zone (Schedule > Zone Default)
 */
export const getEffectiveSettings = async (zoneId: number): Promise<any> => {
  try {
    const api = getCurrentApi();
    const response = await api.get(`${SCHEDULE_URL}/zone/${zoneId}/effective-settings`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching effective settings for zone ${zoneId}:`, error);
    throw error;
  }
};

/**
 * Create a new schedule
 */
export const createSchedule = async (scheduleData: CreateScheduleRequest): Promise<Schedule> => {
  try {
    const api = getCurrentApi();
    const response = await api.post<Schedule>(SCHEDULE_URL, scheduleData);

    // Log the action to Firebase
    await auditService.logAction('Schedule Created', {
      additionalInfo: `Created schedule: ${response.data.name} for ${response.data.zoneName || 'zone ID ' + response.data.zoneId}`
    });

    return response.data;
  } catch (error) {
    console.error('Error creating schedule:', error);
    throw error;
  }
};

/**
 * Update an existing schedule
 */
export const updateSchedule = async (scheduleId: number, scheduleData: UpdateScheduleRequest): Promise<Schedule> => {
  try {
    const api = getCurrentApi();
    const response = await api.put<Schedule>(`${SCHEDULE_URL}/${scheduleId}`, scheduleData);

    // Log the action to Firebase
    await auditService.logAction('Schedule Updated', {
      additionalInfo: `Updated schedule: ${scheduleData.name || 'ID ' + scheduleId}`
    });

    return response.data;
  } catch (error) {
    console.error(`Error updating schedule ${scheduleId}:`, error);
    throw error;
  }
};

/**
 * Delete a schedule
 */
export const deleteSchedule = async (scheduleId: number): Promise<void> => {
  try {
    // Get schedule info before deleting for audit log
    const schedule = await getSchedule(scheduleId);

    const api = getCurrentApi();
    await api.delete(`${SCHEDULE_URL}/${scheduleId}`);

    // Log the action to Firebase
    await auditService.logAction('Schedule Deleted', {
      additionalInfo: `Deleted schedule: ${schedule.name} (ID: ${scheduleId})`
    });
  } catch (error) {
    console.error(`Error deleting schedule ${scheduleId}:`, error);
    throw error;
  }
};

/**
 * Helper function to format schedule days for display
 * @param daysOfWeek Comma-separated list of day numbers (1=Sunday, 7=Saturday)
 * @returns Human-readable days list
 */
export const formatScheduleDays = (daysOfWeek: string): string => {
  const dayNumbers = daysOfWeek
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((day) => day >= 1 && day <= 7);

  if (dayNumbers.length === 7) {
    return 'Every day';
  }

  if (
    dayNumbers.length === 5 &&
    dayNumbers.includes(2) &&
    dayNumbers.includes(3) &&
    dayNumbers.includes(4) &&
    dayNumbers.includes(5) &&
    dayNumbers.includes(6)
  ) {
    return 'Weekdays';
  }

  if (dayNumbers.length === 2 && dayNumbers.includes(1) && dayNumbers.includes(7)) {
    return 'Weekends';
  }

  const dayNames = dayNumbers
    .map((day) => {
      switch (day) {
        case 1:
          return 'Sunday';
        case 2:
          return 'Monday';
        case 3:
          return 'Tuesday';
        case 4:
          return 'Wednesday';
        case 5:
          return 'Thursday';
        case 6:
          return 'Friday';
        case 7:
          return 'Saturday';
        default:
          return '';
      }
    })
    .filter((name) => name);

  return dayNames.join(', ');
};
